import {_} from 'lodash';
import * as ko from "knockout";
import {IDialogOptions} from "../../dialog";
import {
    getCancelButton,
    getHash,
    getNextButton,
    getPreviousButton,
    getSubmitButton
} from "../../../constants/RouteDefinitions";
import {IIsSystemPropertyMutable, ISystemMutable} from "../index";
import {createErrorMessage, getAnyValue, getObjectType, getSyncFieldValue, isPartOf} from "../../../util/misc";
import {ButtonManager} from "../../../util/ButtonManager";
import {MAX_DESCRIPTION_LENGTH, MAX_RESULTS_PER_PAGE, ObjectTypeId, SYSTEM_MEGA_EADM} from "../../../constants";
import {
    IMessageData,
    IObjectData,
    IRuleset,
    ISystem,
    ISystemRole,
    TBaseOrObservableType,
    TSyncfield
} from "../../../index";
import {nsSystemAccess} from "../nsSystemAccess";
import {IComboboxV2Options} from "../../combobox/v2";
import {createServiceListPromise} from "../../combobox/v2-sources";
import PromiseRequest from "../../../req/PromiseRequest";
import {getSearchParamsFn} from "../../../util/request";
import {nsTooltip} from "../../../util/nsTooltip";
import {global, lm} from "../../../appContext";
import {PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {nsRuleset} from "../../ruleset/nsRuleset";
import {nsRoute} from "../../../util/route";
import pubsub from "../../../pubsub";
import ButtonOrder = nsRoute.ButtonOrder;
import groups_to_permissionsHtml from './groups-to-permissions.html?raw';

interface IParams {
    dialog: IDialogOptions
    groups?: TBaseOrObservableType<IObjectData[]>
    system?: TBaseOrObservableType<ISystem>
}

enum Step {
    SystemForm,
    SelectGroups,
    GroupsRuleset,
    Summary,
    Process,
    Finished,
}

enum CollectionRadio {
    Preselected = 'preselected',
    Choose = 'choose',
    Create = 'create',
}

export const template = groups_to_permissionsHtml

export class viewModel {

    private readonly step: KnockoutObservable<Step | undefined> = ko.observable<Step | undefined>(undefined)
    private readonly Step = Step

    private readonly MAX_DESCRIPTION_LENGTH = MAX_DESCRIPTION_LENGTH

    private readonly md: ISystemMutable = nsSystemAccess.createSystemMutable()

    /** Stub system for create-collection form — mutable-systemaccessinfo reads `d.id` / `d.availableto_id`. */
    private readonly d: ISystem = nsSystemAccess.systemMutable2Static(this.md)

    private readonly isPropertyMutable: IIsSystemPropertyMutable = nsSystemAccess.getIsPropertyMutable()

    private readonly pageButtons: ButtonManager = new ButtonManager()
    private readonly groups: KnockoutObservable<IObjectData[]> = ko.observableArray()
    private readonly groupRulesets: KnockoutObservableArray<number> = ko.observableArray<number>([])
    private readonly ObjectTypeId = ObjectTypeId

    private readonly collectionRadio: KnockoutObservable<CollectionRadio | null> = ko.observable(null)
    private readonly CollectionRadio = CollectionRadio

    private readonly req_searchsystem: PromiseRequest = new PromiseRequest('searchsystem')
    private readonly req_createsystem: PromiseRequest = new PromiseRequest('createsystem')
    private readonly requests!: PromiseRequest[]

    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null)
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();

    private readonly selectedSystem: KnockoutObservable<ISystem | null> = ko.observable(null)

    private steps: Step[] = []

    constructor(private params: IParams) {
        const groups = _.cloneDeep(getAnyValue(params.groups))
        this.groups(groups ?? [])

        const ids = this.groups().map(o => o.id)
        this.groupRulesets(ids)

        if (params.system) {
            this.selectedSystem(_.cloneDeep(getAnyValue(params.system)))
            this.collectionRadio(CollectionRadio.Preselected)
        } else {
            this.collectionRadio(CollectionRadio.Choose)
        }

        if (!this.selectedSystem()) {
            this.steps.push(Step.SystemForm)
        }

        if (!this.groups().length) {
            this.steps.push(Step.SelectGroups)
        }

        this.steps.push(Step.GroupsRuleset, Step.Summary, Step.Process, Step.Finished)

        this.initButtons()

        this.step(this.steps[0])
    }

    protected initButtons() {
        this.pageButtons.add(getCancelButton(() => this.params.dialog.doClose?.(), {
            validator:  () => !this.isStep(Step.Finished)
        }))
        this.pageButtons.add({
            onClick: () => this.params.dialog.doClose?.(),
            icon: 'fa-times',
            label: 'close',
            order: ButtonOrder.CANCEL,
            validator:  () => this.isStep(Step.Finished)
        })
        this.pageButtons.add(getPreviousButton({
            onClick: () => this.onPrevClick(),
            enabled: () => this.isPrevEnabled(),
            validator: () => this.isStep(Step.SystemForm, Step.SelectGroups, Step.GroupsRuleset, Step.Summary),
        }))
        this.pageButtons.add(getNextButton({
            onClick: () => this.onNextClick(),
            enabled: () => this.isNextEnabled(),
            validator: () => this.isStep(Step.SystemForm, Step.SelectGroups, Step.GroupsRuleset),
        }))
        this.pageButtons.add(getSubmitButton({
            onClick: this.onSubmit.bind(this),
            validator: () => this.isStep(Step.Summary),
        }))
        this.pageButtons.add({
            label: `open subject|s:((collection|ac))`,
            href: () => getHash('systemaccessdetails', {id: this.selectedSystem()!.id}) ?? '',
            validator: () => !this.params.system && this.isStep(Step.Finished),
            order: ButtonOrder.DETAILS,
        })
    }

    private isValid(): boolean {
        switch (this.step()) {
            case Step.SystemForm: {
                switch (this.collectionRadio()) {
                    case CollectionRadio.Choose:
                        return !!this.selectedSystem()
                    case CollectionRadio.Create:
                        const a = this.md.errName.validate()
                        const b = this.md.errDescription.validate()
                        const c = this.md.errSystemOwner.validate()
                        return a && b && c
                }
            }
            case Step.SelectGroups: {
                return !!this.groups().length
            }
        }
        return true
    }

    private onNextClick() {
        const currentIndex = this.steps.indexOf(this.step()!)
        const step = this.steps[currentIndex + 1]
        if (step != null) {
            this.step(step)
        }
    }

    private onPrevClick() {
        const currentIndex = this.steps.indexOf(this.step()!)
        const step = this.steps[currentIndex - 1]
        if (step != null) {
            this.step(step)
        }
    }

    private isNextEnabled(): boolean {
        const step = this.step();
        if (step == null || this.steps.indexOf(step) >= Step.Summary) {
            return false
        }
        if (!this.isValid()) {
            return false
        }
        return true
    }

    private isPrevEnabled(): boolean {
        const step = this.step();
        if (step == null || this.steps.indexOf(step) == 0) {
            return false
        }
        return true
    }

    private isStep(...steps: Step[]): boolean {
        const step = this.step();
        return step != null && isPartOf(step, ...steps)
    }

    private onSubmit() {
        this.step(Step.Process)
        this.messageData({message: PLEASE_WAIT_MESSAGE})

        return Promise.resolve()
            .then(() => {
                switch (this.collectionRadio()) {
                    case CollectionRadio.Preselected:
                    case CollectionRadio.Choose:
                        this.selectedSystem(this.getSystemSubmitData())
                        break;
                    case CollectionRadio.Create:
                        const _system = this.getSystemSubmitData()
                        const jsystem = JSON.stringify(_system)
                        return this.req_createsystem
                            .request({jsystem: jsystem})
                            .then((data: [ISystem]) => {
                                this.selectedSystem(data[0])
                            })
                }
            })
            .then(() => {
                const groups = _.clone(this.groups())
                const doNext = (): Promise<any> => {
                    const group = groups.shift()
                    if (!group) {
                        return Promise.resolve()
                    }
                    let ruleset: IRuleset
                    return Promise.resolve()
                        .then(() => {
                            if (this.groupRulesets().includes(group.id)) {
                                const _ruleset = nsRuleset.generateRuleset({
                                    name: this.getRulesetName(group),
                                    description: this.getRulesetDescription(group),
                                    objecttype: getObjectType(ObjectTypeId.Group),
                                })
                                const jruleset = JSON.stringify(_ruleset)
                                const p = new PromiseRequest('createruleset')
                                return p
                                    .request({jruleset: jruleset})
                                    .then((data: [IRuleset]) => {
                                        ruleset = data[0]
                                    })
                            }
                        })
                        .then(() => {
                            const _role: ISystemRole = this.getSystemRolesSubmitData(group, ruleset, this.selectedSystem()!)
                            const jrole = JSON.stringify(_role)
                            const p = new PromiseRequest('createrole')
                            return p
                                .request({
                                    jrole: jrole
                                })
                        })
                        .then(() => {
                            return doNext()
                        })
                }
                return doNext()
            })
            .then(() => {
                // cleanup
                global.cacheManager.removeCache('getobject');
                global.cacheManager.removeCache('getruleset');
                global.cacheManager.removeCache('searchobject');
                global.cacheManager.removeCache('searchruleset');
                global.cacheManager.removeCache('searchsystem');
                this.step(Step.Finished)
                const message = this.collectionRadio() == CollectionRadio.Create
                    ? 'Collection and permissions have been created'
                    : 'Permissions have been created'
                this.messageData({message: lm.t(message) ?? '', type: 'success'})
                pubsub.trigger('object.needs.refresh');
            })
            .catch((reason: string) => {
                this.onPrevClick()
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    private getSyncfieldValue(key: TSyncfield, obj: IObjectData) {
        return getSyncFieldValue(key, obj.syncfields)
    }

    private getComboboxCollectionParams(): IComboboxV2Options<ISystem> {
        const listSource = createServiceListPromise<ISystem>({
            name: 'searchsystem',
            params: getSearchParamsFn('searchsystem')({resultsprpage: MAX_RESULTS_PER_PAGE}),
        })
        return {
            inputAttrs: {placeholder: lm.t('Choose|td') ?? ''},
            option: {
                label: o => o?.name ?? '',
                tooltip: o => lm.t(nsTooltip.getTitle_system(o!) ?? '') ?? '',
                constrain: o => o != null && o.name != SYSTEM_MEGA_EADM && !o.definitionreadonly,
            },
            promise: listSource.promise,
            onDispose: listSource.onDispose,
            selected: this.selectedSystem,
        }
    }

    private getTitle_objectdata(obj: IObjectData) {
        return lm.t(nsTooltip.getTitle_objectdata(obj) ?? '') ?? ''
    }

    private getSystemSubmitData(): ISystem {
        switch (this.collectionRadio()) {
            case CollectionRadio.Create:
                return nsSystemAccess.systemMutable2Static(this.md)
            case CollectionRadio.Choose:
            default:
                return this.selectedSystem()!;
        }
    }

    private getSystemRolesSubmitData(group: IObjectData, ruleset: IRuleset, system: ISystem): ISystemRole {
        const ret: ISystemRole = {
            availableto: '',
            availableto_id: 0,
            availabletorulesets: '',
            description: '',
            id: 0,
            memberofgroup: group.id,
            memberofgroups: String(group.id),
            memberruleset: null,
            metadata: '',
            name: this.getRoleName(group),
            overrides: [],
            priority: 0,
            purgemanualdepartmentpermissions: false,
            recursive: false,
            roleenum: 0,
            rolememberruleset: ruleset?.id ?? 0,
            rolememberrulesets: String(ruleset?.id ?? ''),
            systemid: system.id,
            systemowner: '',
        }
        return ret
    }

    private getRoleName(group: IObjectData): string {
        return lm.t(`((Access)): ${this.getSyncfieldValue('Name', group)}`) ?? ''
    }

    private getRulesetName(group: IObjectData): string {
        return lm.t(`Access: was automatically granted access to group|group:${this.getSyncfieldValue('Name', group)}`) ?? ''
    }

    private getRulesetDescription(group: IObjectData): string {
        return lm.t(`This ruleset can be used to automatically assign access to permission|permission:${this.getRoleName(group)}`) ?? ''
    }

    private doAllGroupsHaveRulesets(): boolean {
        return this.groups().length == this.groupRulesets().length
    }

    private doSomeGroupsHaveRulesets(): boolean {
        return !this.doAllGroupsHaveRulesets() && !!this.groupRulesets().length
    }

    private selectAllGroupRulests = ko.computed({
        read: (): boolean => {
            return this.doAllGroupsHaveRulesets()
        },
        write: (bool: boolean) => {
            if (bool) {
                const ids = this.groups().map(o => o.id)
                this.groupRulesets(ids)
            } else {
                this.groupRulesets([])
            }
        }
    });

    dispose() {
        this.req_searchsystem.abort()
        this.req_createsystem.abort()
        this.requests?.forEach(r => r.abort())
        this.kosub.unsuscribeAll()
        this.selectAllGroupRulests.dispose()
    }

}